/**
 * `/waitlist/status?token=…` — public-safe view of a waitlist application.
 *
 * The token in the URL is a single-use magic-link-style token, emailed
 * after a `/auth/magic-link` request for an email that's on the waitlist
 * but doesn't have a user account. The server returns the sanitized row
 * (no `whyYou`, no internal IDs) on the first POST and rejects subsequent
 * requests with the same token.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { WaitlistStatusResponse } from '@eloquor/api-contract';
import { AuthShell } from '../components/layout/AuthShell';
import { getWaitlistStatus } from '../api/waitlist';
import { ApiCallError } from '../api/client';

export function WaitlistStatusPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ok'; data: WaitlistStatusResponse }
    | { status: 'error'; message: string }
  >({ status: 'loading' });
  // The status token is single-use; in React 18 StrictMode the effect would
  // fire twice and the second call would always see "invalid token". The
  // ref keeps us to a single fetch for the lifetime of the page mount.
  // Same pattern used by DemoPage.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    if (!token) {
      startedRef.current = true;
      setState({ status: 'error', message: 'Missing token. Use the link from your email.' });
      return;
    }
    startedRef.current = true;
    let cancelled = false;
    void getWaitlistStatus(token)
      .then((data) => {
        if (!cancelled) setState({ status: 'ok', data });
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err instanceof ApiCallError
            ? err.apiError.message
            : 'Could not load your waitlist status.';
        setState({ status: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <AuthShell topRight={<Link to="/sign-in">Sign in</Link>}>
      <div className="auth-card">
        <div className="auth-eyebrow">
          <span className="num">02·b</span> · Waitlist status
        </div>

        {state.status === 'loading' && (
          <>
            <h1 className="auth-title">
              Loading <em>status…</em>
            </h1>
          </>
        )}

        {state.status === 'error' && (
          <>
            <h1 className="auth-title">
              Link <em>invalid.</em>
            </h1>
            <p className="auth-sub">{state.message}</p>
            <p className="below-card">
              Not you? <Link to="/waitlist">Apply for the waitlist</Link>
            </p>
          </>
        )}

        {state.status === 'ok' && (
          <>
            <h1 className="auth-title">
              {state.data.status === 'accepted' ? (
                <>
                  You're <em>in.</em>
                </>
              ) : (
                <>
                  Still on the <em>list.</em>
                </>
              )}
            </h1>
            <p className="auth-sub">
              {state.data.status === 'accepted' ? (
                <>
                  We've already sent you an invite — check your inbox for an email from Eloquor with a redemption code.
                </>
              ) : (
                <>
                  We'll email an invite when a seat opens. No follow-ups until then.
                </>
              )}
            </p>

            <div style={{ marginTop: 8 }}>
              <div className="detail-row">
                <span className="k">Name</span>
                <span className="v">{state.data.name}</span>
              </div>
              <div className="detail-row">
                <span className="k">Email</span>
                <span className="v mono">{state.data.email}</span>
              </div>
              <div className="detail-row">
                <span className="k">School</span>
                <span className="v">{state.data.school}</span>
              </div>
              <div className="detail-row">
                <span className="k">Event</span>
                <span className="v">{state.data.event}</span>
              </div>
              <div className="detail-row">
                <span className="k">Circuit</span>
                <span className="v">{state.data.circuit}</span>
              </div>
              <div className="detail-row">
                <span className="k">Applied</span>
                <span className="v mono">
                  {new Date(state.data.appliedAt).toLocaleDateString()}
                </span>
              </div>
            </div>

            <p className="below-card">
              Not you? <Link to="/waitlist">Apply for the waitlist</Link>
            </p>
          </>
        )}
      </div>
    </AuthShell>
  );
}
